import { useState, useEffect } from 'react';
import { Truck, Receipt, Share2, Contact, Loader2, Save } from 'lucide-react';
import { configurationsService } from '../api/services';
import type { Configuration } from '../types';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Skeleton } from './ui/skeleton';

const GROUP_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  shipping: {
    label: 'Shipping Configuration',
    icon: <Truck className="w-5 h-5 text-blue-600" />,
    color: 'bg-blue-50',
  },
  tax: {
    label: 'Tax Configuration',
    icon: <Receipt className="w-5 h-5 text-green-600" />,
    color: 'bg-green-50',
  },

  social_media: {
    label: 'Social Media URLs',
    icon: <Share2 className="w-5 h-5 text-purple-600" />,
    color: 'bg-purple-50',
  },
  contact: {
    label: 'Contact Details',
    icon: <Contact className="w-5 h-5 text-orange-600" />,
    color: 'bg-orange-50',
  },
};

const GROUP_ORDER = ['shipping', 'tax', 'social_media', 'contact'];

function isBooleanConfig(config: Configuration): boolean {
  return config.value === 'true' || config.value === 'false';
}

export function Configurations() {
  const [configs, setConfigs] = useState<Configuration[]>([]);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingGroups, setSavingGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const data = await configurationsService.getConfigurations();
      setConfigs(data);
      const values: Record<string, string> = {};
      data.forEach((c) => {
        values[c.key] = c.value;
      });
      setEditedValues(values);
    } catch (error) {
      console.error('Failed to fetch configurations:', error);
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggle = (key: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: prev[key] === 'true' ? 'false' : 'true',
    }));
  };

  const getGroupedConfigs = (): Record<string, Configuration[]> => {
    const grouped: Record<string, Configuration[]> = {};
    for (const config of configs) {
      const group = config.config_group || 'other';
      if (group === 'payment') continue;
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(config);
    }
    return grouped;
  };

  const hasGroupChanges = (groupConfigs: Configuration[]): boolean => {
    return groupConfigs.some((c) => editedValues[c.key] !== c.value);
  };

  const handleSaveGroup = async (groupKey: string, groupConfigs: Configuration[]) => {
    const changed = groupConfigs.filter((c) => editedValues[c.key] !== c.value);
    if (changed.length === 0) return;

    setSavingGroups((prev) => ({ ...prev, [groupKey]: true }));

    try {
      const promises = changed.map((c) =>
        configurationsService.updateConfiguration(c.key, editedValues[c.key])
      );
      await Promise.all(promises);

      setConfigs((prev) =>
        prev.map((c) =>
          editedValues[c.key] !== undefined ? { ...c, value: editedValues[c.key] } : c
        )
      );

      toast.success(`${GROUP_META[groupKey]?.label || groupKey} saved successfully`);
    } catch (error) {
      console.error('Failed to save configurations:', error);
      toast.error('Failed to save configurations');
    } finally {
      setSavingGroups((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const grouped = getGroupedConfigs();

  return (
    <div className="space-y-6">
      {GROUP_ORDER.map((groupKey) => {
        const groupConfigs = grouped[groupKey];
        if (!groupConfigs || groupConfigs.length === 0) return null;

        const meta = GROUP_META[groupKey] || {
          label: groupKey,
          icon: null,
          color: 'bg-gray-50',
        };
        const saving = savingGroups[groupKey] || false;
        const hasChanges = hasGroupChanges(groupConfigs);

        return (
          <Card key={groupKey}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${meta.color} rounded-lg`}>{meta.icon}</div>
                  <div>
                    <CardTitle>{meta.label}</CardTitle>
                    <CardDescription>
                      {groupConfigs.length} configuration{groupConfigs.length !== 1 ? 's' : ''}
                    </CardDescription>
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={saving || !hasChanges}
                  onClick={() => handleSaveGroup(groupKey, groupConfigs)}
                >
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groupConfigs.map((config) => (
                  <div key={config.key} className="space-y-2">
                    {isBooleanConfig(config) ? (
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label>{config.config_display_name || config.key}</Label>
                          {config.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {config.description}
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={editedValues[config.key] === 'true'}
                          onCheckedChange={() => handleToggle(config.key)}
                        />
                      </div>
                    ) : (
                      <>
                        <Label htmlFor={config.key}>
                          {config.config_display_name || config.key}
                        </Label>
                        <Input
                          id={config.key}
                          value={editedValues[config.key] || ''}
                          onChange={(e) => handleValueChange(config.key, e.target.value)}
                          placeholder={config.description || ''}
                        />
                        {config.description && (
                          <p className="text-xs text-muted-foreground">{config.description}</p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Render any ungrouped configs */}
      {Object.entries(grouped)
        .filter(([key]) => !GROUP_ORDER.includes(key))
        .map(([groupKey, groupConfigs]) => (
          <Card key={groupKey}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="capitalize">{groupKey.replace(/_/g, ' ')}</CardTitle>
                <Button
                  size="sm"
                  disabled={savingGroups[groupKey] || !hasGroupChanges(groupConfigs)}
                  onClick={() => handleSaveGroup(groupKey, groupConfigs)}
                >
                  {savingGroups[groupKey] ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groupConfigs.map((config) => (
                  <div key={config.key} className="space-y-2">
                    <Label htmlFor={config.key}>
                      {config.config_display_name || config.key}
                    </Label>
                    <Input
                      id={config.key}
                      value={editedValues[config.key] || ''}
                      onChange={(e) => handleValueChange(config.key, e.target.value)}
                      placeholder={config.description || ''}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
